import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { blogPosts } from '../data/mock';
import { ChevronLeft, Calendar, User } from 'lucide-react';
import SEO from '../components/common/SEO';
import { getImageUrl } from '../utils/imageUrl';

const NewsPage = () => {
  const { slug } = useParams();

  // Single blog post view
  if (slug) {
    const post = blogPosts.find(p => p.slug === slug);

    if (!post) {
      return (
        <div className="min-h-screen bg-white">
          <Header />
          <main className="py-20 text-center">
            <h1 className="text-2xl text-gray-600">Post not found</h1>
            <Link to="/news" className="mt-4 inline-block text-black underline">Back to News</Link>
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-white">
        <SEO 
          url={`/news/${slug}`}
          title={post.title}
          description={post.excerpt}
          image={getImageUrl(post.image)}
          type="article"
          article={{
            title: post.title,
            excerpt: post.excerpt,
            image: getImageUrl(post.image),
            date: post.date,
            author: post.author
          }}
        />
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back Link */}
          <nav className="mb-8">
            <Link to="/news" className="inline-flex items-center text-sm text-gray-500 hover:text-black transition-colors">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to News
            </Link>
          </nav>

          {/* Article Header */}
          <article>
            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                {post.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {post.date}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            {post.image && (
              <div className="aspect-video overflow-hidden rounded-lg mb-8">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 leading-relaxed text-lg">
                {post.excerpt}
              </p>
              <p className="text-gray-600 leading-relaxed mt-6">
                {post.content}
              </p>
              <p className="text-gray-600 leading-relaxed mt-6">
                Art has the power to transform spaces and evoke deep emotions. Whether you're a seasoned collector 
                or just beginning your journey into fine art, understanding the nuances of size, color, and composition 
                can help you make choices that truly resonate with your space and sensibilities.
              </p>
              <p className="text-gray-600 leading-relaxed mt-6">
                Stay tuned for more insights, behind-the-scenes looks at my creative process, and updates on 
                new collections and limited edition releases.
              </p>
            </div>
          </article>

          {/* More Posts */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-light text-gray-900 mb-8" style={{ fontFamily: 'Georgia, serif' }}>
              More from the Blog
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {blogPosts
                .filter(p => p.id !== post.id)
                .slice(0, 2)
                .map((relatedPost) => (
                  <Link key={relatedPost.id} to={`/news/${relatedPost.slug}`} className="group">
                    {relatedPost.image && (
                      <div className="aspect-video overflow-hidden rounded-lg mb-4">
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <h3 className="text-lg font-medium text-gray-900 group-hover:underline line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-2">{relatedPost.date}</p>
                  </Link>
                ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Blog listing view
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        url="/news"
        title="News & Blog"
        description="Art insights, studio stories, and updates from Swedish artist Gorito. Learn about new collections, artistic process, and fine art print releases."
      />
      <Header />
      <main>
        {/* Page Header */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-light text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
              News & Blog
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              Insights, stories, and updates from the studio
            </p>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link key={post.id} to={`/news/${post.slug}`} className="group">
                {post.image && (
                  <div className="aspect-video overflow-hidden rounded-lg mb-4">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <h2 className="text-xl font-medium text-gray-900 group-hover:underline line-clamp-2 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                  {post.title}
                </h2>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">
                  {post.date}
                </p>
                <p className="text-sm text-gray-600 line-clamp-3">
                  {post.excerpt}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NewsPage;
